import { createSelector } from 'reselect';

import { TIME_FORMATS } from 'constants/timeformats';
import { dateFormat } from 'helpers/day';
import { getContractSymbolForOptions } from 'helpers/optionsChain';
import { isEmpty, mergeWith } from 'helpers/ramda';
import { numberCommaSeparator, optionsTypes } from 'helpers/utils';
import i18n from 'i18n/config';
import { OiChangeData, OpenInterestChangeChartParams } from 'reducers/optionsAnalytics';
import { oiCallValueSelector, oiPutValueSelector } from 'selectors/priceSelectors';
import { productsListSelector } from 'selectors/tradeSelectors';
import { ChartSupportedAssets, OiChartTimePeriodType } from 'types/actions/charts';
import { Product } from 'types/IProducts';
import IState from 'types/Istore';
import type {
  OIChangeTimePeriod,
  OIChangeDataWithProductInfo,
  AggregateMultipleExpiriesDataForOptions,
  OIChangeChartPoints,
  OIChangeChartDataPoints,
  FormattedExpiries,
  AssetWiseExpiries,
  Expiries,
} from 'types/optionsAnalytics';
import { wrapSelector } from 'variableStore/helpers';

import { assetWiseExpiriesSelector, formattedExpiriesSelector } from './common';

const oiChangeChartParamsSelector = (state: IState): OpenInterestChangeChartParams =>
  state.optionsAnalytics.oiChangeChart;

const oiChangeActiveAssetSymbolSelector = createSelector(
  [oiChangeChartParamsSelector],
  (oiChangeChartParams: OpenInterestChangeChartParams): ChartSupportedAssets =>
    oiChangeChartParams.activeAssetSymbol
);

const oiChangeActiveTimePeriod = (state: IState): OiChartTimePeriodType =>
  state.optionsAnalytics.oiChangeChart.timePeriod;

const oiChangeExpiriesSelector = (state: IState): Expiries =>
  state.optionsAnalytics.oiChangeChart.expiries;

const oiChangeAllExpiriesSelector = createSelector(
  [oiChangeExpiriesSelector],
  (expiries: Expiries): boolean => Object.values(expiries)?.every(expiry => expiry)
);

const oiChangeDataSelector = (state: IState): OiChangeData =>
  state.optionsAnalytics.oiChangeChart.data;

const optionsProductsSelector = createSelector(
  [productsListSelector],
  (products: Product[]): Product[] =>
    products.filter(
      product =>
        product.contract_type === optionsTypes.CALL ||
        product.contract_type === optionsTypes.PUT
    )
);

/**
 * Function to get options data with product information.
 * It maps the open interest change data to their respective product details.
 */
const getOptionsDataWithProductInfo = ({
  activeExpiryData,
  activeAssetSymbol,
  activeExpiry,
  optionsProducts,
  oiData,
  contractType,
}: {
  activeExpiryData: { [strike: string]: number };
  activeAssetSymbol: ChartSupportedAssets;
  activeExpiry: string;
  optionsProducts: Product[];
  oiData: Record<string, string>;
  contractType: 'calls' | 'puts';
}): OIChangeDataWithProductInfo => {
  return Object.entries(activeExpiryData).reduce(
    (callDataWithProductInfo, [strikePrice, oiChange]) => {
      // Generate the contract symbol for the current option
      const contractSymbol = getContractSymbolForOptions({
        assetSymbol: activeAssetSymbol,
        strikePrice,
        contractType,
        expiryDate: activeExpiry,
      });

      // Find the product details for the current contract symbol
      const currentProduct =
        optionsProducts.find(({ symbol }) => symbol === contractSymbol) || {};

      return {
        ...callDataWithProductInfo,
        [strikePrice]: {
          productId: currentProduct.id,
          oi: oiData[currentProduct.id as number],
          oiChange,
          symbol: contractSymbol,
        },
      };
    },
    {}
  );
};

/**
 * Function to aggregate multiple expiries data for options.
 * It merges the open interest change data of active expiry with the aggregate data.
 */
const getAggregateMulipleExpiriesData = ({
  optionsDataWithProductInfo,
  aggregateMultipleExpriesData,
}: {
  optionsDataWithProductInfo: OIChangeDataWithProductInfo;
  aggregateMultipleExpriesData: OIChangeDataWithProductInfo;
}): OIChangeDataWithProductInfo => {
  return mergeWith(
    (activeExpiryProduct, aggregateData) => {
      // If either product is missing, return the available one
      if (!activeExpiryProduct || !aggregateData) {
        return activeExpiryProduct || aggregateData;
      }

      // Determine which product has the larger open interest
      const isActiveProductHaveBiggerOi =
        Number(activeExpiryProduct.oi) > Number(aggregateData.oi);
      const finalProduct = isActiveProductHaveBiggerOi
        ? activeExpiryProduct
        : aggregateData;

      // Return the merged product data
      return {
        productId: finalProduct.productId,
        oiChange: (activeExpiryProduct.oiChange || 0) + (aggregateData.oiChange || 0),
        oi: finalProduct.oi,
        symbol: finalProduct.symbol,
      };
    },
    optionsDataWithProductInfo,
    aggregateMultipleExpriesData
  );
};

const chartDataPointsSelector = wrapSelector(
  [
    oiChangeDataSelector,
    oiChangeActiveTimePeriod,
    oiChangeExpiriesSelector,
    optionsProductsSelector,
    oiChangeActiveAssetSymbolSelector,
    oiCallValueSelector,
    oiPutValueSelector,
  ],
  (
    data: OiChangeData,
    timePeriod: OIChangeTimePeriod,
    expiries: Expiries,
    optionsProducts: Product[],
    activeAssetSymbol: ChartSupportedAssets,
    oiCall: Record<string, string>,
    oiPut: Record<string, string>
  ): OIChangeChartDataPoints => {
    // Get current time period data
    const currentData = data[timePeriod];
    const activeAssetExpiries = expiries[activeAssetSymbol];

    // If expiries or current data not available, return empty array
    if (isEmpty(expiries) || isEmpty(currentData)) {
      return { callDataPoints: [], putDataPoints: [] };
    }

    // Filter and format active expiries
    const activeExpiries = Object.keys(activeAssetExpiries)
      .filter(expiry => activeAssetExpiries[expiry])
      .map(expiry => dateFormat(Number(expiry), TIME_FORMATS.DDMMYY));

    // Aggregate data for calls and puts across multiple expiries
    const { call, put }: AggregateMultipleExpiriesDataForOptions = activeExpiries.reduce(
      (aggregateData, activeExpiry) => {
        const activeExpiryData = currentData[activeExpiry];
        if (!activeExpiryData) {
          return aggregateData;
        }

        const { call: activeExpiryCallData, put: activeExpiryPutData } = activeExpiryData;

        // Get call data with product information
        const callDataWithProductInfo = getOptionsDataWithProductInfo({
          activeExpiryData: activeExpiryCallData || {},
          activeAssetSymbol,
          activeExpiry,
          optionsProducts,
          oiData: oiCall,
          contractType: 'calls',
        });

        // Get put data with product information
        const putDataWithProductInfo = getOptionsDataWithProductInfo({
          activeExpiryData: activeExpiryPutData || {},
          activeAssetSymbol,
          activeExpiry,
          optionsProducts,
          oiData: oiPut,
          contractType: 'puts',
        });

        // Aggregate call data across multiple expiries
        const aggregateMultipleExpiriesCallData = getAggregateMulipleExpiriesData({
          optionsDataWithProductInfo: callDataWithProductInfo,
          aggregateMultipleExpriesData: aggregateData.call,
        });

        // Aggregate put data across multiple expiries
        const aggregateMultipleExpiriesPutData = getAggregateMulipleExpiriesData({
          optionsDataWithProductInfo: putDataWithProductInfo,
          aggregateMultipleExpriesData: aggregateData.put,
        });

        return {
          call: aggregateMultipleExpiriesCallData,
          put: aggregateMultipleExpiriesPutData,
        };
      },
      {
        call: {},
        put: {},
      } as AggregateMultipleExpiriesDataForOptions
    );

    // Map call data to chart points
    const callDataPoints: OIChangeChartPoints[] = Object.entries(call).map(
      ([strike, { productId, oiChange, oi, symbol }]) => {
        return {
          contractType: 'calls',
          name: strike,
          tooltipHeaderValue: `At Strike: $ ${numberCommaSeparator(strike)}`,
          tooltipPointValue: `$${oiChange}`,
          y: oiChange,
          productId,
          oi,
          symbol,
        };
      }
    );

    // Map put data to chart points
    const putDataPoints: OIChangeChartPoints[] = Object.entries(put).map(
      ([strike, { productId, oiChange, oi, symbol }]) => {
        return {
          contractType: 'puts',
          name: strike,
          tooltipHeaderValue: `At Strike: $${strike}`,
          tooltipPointValue: `$${oiChange}`,
          y: oiChange,
          productId,
          oi,
          symbol,
        };
      }
    );

    // Return the chart data points for calls and puts
    return { callDataPoints, putDataPoints };
  }
);

/**
 * Selector to get the active expiries labels as a comma-separated string
 *
 * @sample 'D1, D2, D3'
 */
const activeExpiriesLabelSelector = createSelector(
  [
    oiChangeExpiriesSelector,
    formattedExpiriesSelector,
    oiChangeActiveAssetSymbolSelector,
    assetWiseExpiriesSelector,
  ],
  (
    expiries: Expiries,
    formattedExpiries: FormattedExpiries,
    activeAssetSymbol: ChartSupportedAssets,
    assetWiseExpiries: AssetWiseExpiries
  ): string => {
    const activeAssetSelectedExpiries = expiries[activeAssetSymbol];
    const selectedExpiryKeys = Object.keys(activeAssetSelectedExpiries).filter(
      timestamp => activeAssetSelectedExpiries[timestamp]
    );

    // Check if no expiry is selected
    if (selectedExpiryKeys.length === 0) {
      return i18n.t('optionsAnalyticsDashboard:selectExpiry');
    }

    // Check if all expiries are selected
    if (selectedExpiryKeys.length === assetWiseExpiries[activeAssetSymbol].length) {
      return i18n.t('optionsAnalyticsDashboard:all');
    }
    // Filter selected expiries and map them to their formatted labels
    return Object.keys(expiries[activeAssetSymbol])
      .filter(timestamp => expiries[activeAssetSymbol][timestamp]) // Keep only selected expiries
      .map(timestamp => formattedExpiries[timestamp])
      .join(', ') // Combine labels into a single string
      .toUpperCase();
  }
);

export {
  oiChangeChartParamsSelector,
  oiChangeActiveAssetSymbolSelector,
  oiChangeActiveTimePeriod,
  oiChangeExpiriesSelector,
  oiChangeAllExpiriesSelector,
  oiChangeDataSelector,
  chartDataPointsSelector,
  activeExpiriesLabelSelector,
  optionsProductsSelector,
};
